import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { generateHeaders, getBaseUrl, getUserToken } from '../../../utils/helpers.js'
import { Section } from 'lucide-react'
import Header from '../../../components/Header.tsx'
import Footer from '../../../components/Footer.tsx'

import Modal from 'react-modal'
import { faL } from '@fortawesome/free-solid-svg-icons'


/* REQUIRED once in your app (safe here too if not done globally) */
Modal.setAppElement('#root')

const OrderPage = () => {
  const { planId } = useParams()
  const navigate = useNavigate()
  const [loadingOrder, setLoadingOrder] = useState(true)
  const [plan, setPlan] = useState(null)
  const [userPlan, setUserPlan] = useState(null)
  const [paymentProviders, setPaymentProviders] = useState([])
  const [paymentProvider, setPaymentProvider] = useState({})
  const [billingCycles, setBillingCycles] = useState([])
  const [billingCycle, setBillingCycle] = useState({})
  const [loadingTotalAmount, setLoadingTotalAmount] = useState(false)
  const [totalAmount, setTotalAmount] = useState(null)

  const [creatingOrder, setCreatingOrder] = useState(false)
  const [createOrderError, setCreateOrderError] = useState(null)

  const userToken = getUserToken()
  const baseUrl = getBaseUrl()
  const headers = generateHeaders()

  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    fetchUserPlan()
  }, [])

  useEffect(() => {
    if (planId) {
      //TODO: Query Plan Cycle Prices
      fetchPlan()
      fetchBillingCycles()
      fetchProviders()
    }
  }, [planId])

  const fetchPlan = () => {
    fetch(`${baseUrl}/api/plans/${planId}?fields=id,name,price`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setPlan(data)
      })
  }

  const fetchBillingCycles = () => {
    fetch(`${baseUrl}/api/billingCycles?planId=${planId}&fields=id,cycle,price,planId`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.billingCycles?.length) {
          setBillingCycles(data.billingCycles)
          setBillingCycle(data.billingCycles[0])
          fetchTotalAmount(data.billingCycles[0].id)
        }
      })
  }

  const fetchProviders = () => {
    fetch(`${baseUrl}/api/providers`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.providers?.length) {
          setPaymentProviders(data.providers)
          setPaymentProvider(data.providers[0])
        }
      })
  }

  const fetchUserPlan = () => {
    fetch(`${baseUrl}/api/userPlans/current`, {
      headers
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setUserPlan(data)
      })
  }

  const fetchTotalAmount = (billingCycleId) => {
    setLoadingTotalAmount(true)
    fetch(`${baseUrl}/api/orders/price`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ billingCycleId }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.totalPrice) {
          setTotalAmount(data.totalPrice.toFixed(2))
        }
        setLoadingTotalAmount(false)
      })
  }


  const submitOrder = () => {

    setIsModalOpen(false)
    setCreatingOrder(true)

    const order = {
      planId: plan.id,
      planName: plan.name,
      billingCycleId: billingCycle.id,
      billingCycle: billingCycle.cycle,
      providerId: paymentProvider.id,
      providerName: paymentProvider.name,
      totalAmount: totalAmount,
    }

    fetch(`${baseUrl}/api/orders`, {
      method: 'POST',
      headers,
      body: JSON.stringify(order),
    })
      .then((res) => {
        if (res.ok) return res.json()
        throw new Error(res.statusText)
      })
      .then((data) => {
        const orderId = data.response.id
        navigate(`/order/checkout/${orderId}`)
      })
      .catch(() => {
        setCreatingOrder(false)
        setCreateOrderError('Error creating order, please try again')
      })

  }

  const createOrder = () => {

    if (userPlan) {
      setIsModalOpen(true)
      return
    }
    submitOrder()

  }

  const selectBillingCycle = (item) => {
    setBillingCycle(item)
    fetchTotalAmount(item.id)
  }

  return (
    <section>
      <Header />
      <div className="blog padding-top padding-bottom section-bg-color mt-5">
        <div className="container mt-5">
          <div className="row g-5 mt-5">
            <div className="col-lg-8">
              {/* Billing Cycles Section */}
              <div className="row g-4 align-items-center" style={{ marginBottom: '3.5rem' }}>
                <h4>Product Billing Cycle</h4>
                {billingCycles.map((item) => (
                  <div
                    className="col-md-6 col-lg-4"
                    style={{ cursor: 'pointer' }}
                    onClick={() => selectBillingCycle(item)}
                    key={item.id}
                  >
                    <div className="pricing__item">
                      <div
                        className={
                          item.id === billingCycle.id
                            ? 'pricing__item-inner active'
                            : 'pricing__item-inner'
                        }
                      >
                        <div className="pricing__item-content">
                          <div className="pricing__item-top">
                            <h6 className="mb-15">{item.cycle}</h6>
                            <h3 className="mb-25">${item.price}</h3>
                          </div>
                          <div className="pricing__item-bottom">
                            <div
                              className={
                                item.id === billingCycle.id
                                  ? 'trk-btn trk-btn--outline active'
                                  : 'trk-btn trk-btn--outline'
                              }
                            >
                              {item.id === billingCycle.id ? 'Selected' : 'Select'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Providers Section */}
              <div className="row g-4 align-items-center">
                <h4>Payment Providers</h4>
                {paymentProviders.map((item) => (
                  <div
                    className="col-md-6 col-lg-4"
                    key={item.id}
                    onClick={() => setPaymentProvider(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="pricing__item">
                      <div
                        className={`pricing__item-inner ${paymentProvider.id === item.id ? 'active' : ''
                          }`}
                      >
                        <div className="pricing__item-content">
                          <div className="pricing__item-top">
                            <img
                              src={`/images/payment-providers/${item.name.toLowerCase()}.png`}
                              alt={item.name}
                              style={{ maxWidth: '100px', height: 'auto' }}
                              onError={(e) => {
                                e.target.src = '/images/payment-providers/default.png'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="col-lg-4 col-md-8 col-12">
              <div className="sidebar">
                <div className="row g-4">
                  <div className="col-12">
                    <div className="sidebar__categorie">
                      <div className="sidebar__head">
                        <h6>Order Summary</h6>
                      </div>
                      <div className="sidebar__categorie-body">
                        <div className="sidebar__categorie-content">
                          <ul>
                            <li>
                              <span>Plan</span>
                              <span>{plan?.name || 'N/A'}</span>
                            </li>
                            <li>
                              <span>Plan Price</span>
                              <span>${plan?.price}/mo</span>
                            </li>
                            <li>
                              <span>Billing Cycle</span>
                              <span>{billingCycle?.cycle || 'N/A'}</span>
                            </li>
                            <li>
                              <span>Plan Cycle Price</span>
                              <span>${billingCycle?.price || 'N/A'}</span>
                            </li>
                            <li>
                              <span>Payment Method</span>
                              <span>{paymentProvider?.name || 'N/A'}</span>
                            </li>
                            <li className="active">
                              <span>Total Amount</span>
                              <span>
                                {loadingTotalAmount ? 'Calculating...' : `$${totalAmount || '0.00'}`}
                              </span>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="trk-btn trk-btn--border trk-btn--primary d-block mt-4"
                                onClick={createOrder}
                                disabled={creatingOrder || loadingTotalAmount}
                              >
                                {creatingOrder ? 'Creating Order...' : 'Proceed to Checkout'}
                              </button>
                              {createOrderError && (
                                <div
                                  style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}
                                >
                                  {createOrderError}
                                </div>
                              )}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* MODAL */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999, // 🔥 ensures overlay is above everything
          },
          content: {
            position: 'relative',
            inset: 'auto',
            padding: '2rem',
            width: '100%',
            borderRadius: '8px',
            background: 'transparent',
            border: 'None',
            zIndex: 10000, // 🔥 modal content above overlay
          },
        }}
      >
        <>
          <div className="row">
            <div className="col-12">
              <div className="account__content account__content--style1">
                <div className="account__header">
                  <h2>Confirmation</h2>

                  <p>
                    You alredy hava an active plan. By proceeding with this order, Your current active plan will be
                    overwritten by the new one. Do you wish to continue?

                  </p>

                </div>

                <div className="account__divider account__divider--style1">
                  <span></span>
                </div>

                <div className='row'>
                  <div className='col-6'>
                    <button
                      type="button"
                      className="trk-btn trk-btn--border trk-btn--primary d-block"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className='col-6'>
                    <button
                      type="button"
                      className="trk-btn trk-btn--outline d-block"
                      onClick={() => submitOrder()}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      </Modal>
      <Footer />
    </section>
  )
}

export default OrderPage
